<!-- eslint-disable default-case -->
<script setup lang="ts">
import UIButtonPrimary from '@ui-components/UIButton/UIButtonPrimary.vue';
import UIButtonSecondary from '@ui-components/UIButton/UIButtonSecondary.vue';
import UIButtonOld from '@ui-components/UIButtonOld/UIButtonOld.vue';

import { useSlots } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { ButtonType, ButtonTheme, ButtonSize } from './';
import type { ButtonColor } from '@ui-components/UIButtonOld';

export interface Props {
  type?: ButtonType;
  theme?: ButtonTheme;
  size?: ButtonSize;
  color?: ButtonColor,
  isLoading?: boolean;
  isDisabled?: boolean;
  isIcon?: boolean;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  theme: 'primary',
  size: 'md',
  color: 'green',
  isLoading: false,
  isDisabled: false,
  isIcon: false,
  ariaLabel: '',
});

const slots = useSlots();
</script>

<template>
  <component
    :is="props.theme === 'primary' ? UIButtonPrimary : UIButtonSecondary"
    v-if="props.theme === 'primary' || props.theme === 'secondary'"
    :type="type"
    :disabled="isDisabled"
    :is-disabled="isDisabled"
    :aria-label="ariaLabel"
    class="rounded-full text-blue-700 font-bold transition inline-flex justify-center items-center"
    :class="[
      {'px-6 py-3 text-sm': props.size === 'sm'},
      {'px-8 py-4 text-base': props.size === 'md'},
      {'px-12 py-4 text-lg': props.size === 'lg'},
      {'!p-0 grid place-items-center': props.isIcon},
      {'size-10': props.size === 'sm' && props.isIcon},
      {'size-12': props.size === 'md' && props.isIcon},
      {'size-16 text-3xl': props.size === 'lg' && props.isIcon},
      {'cursor-not-allowed bg-gray-100 !text-gray-700': isDisabled},
    ]"
  >
    <span v-if="isLoading" aria-busy class="relative">
      <FontAwesomeIcon :icon="['fas', 'spinner']" size="lg" class="animate-spin" />
    </span>

    <span v-else-if="slots['icon-start']" class="relative mr-2">
      <slot name="icon-start" />
    </span>

    <div v-if="!isLoading" class="relative">
      <slot />
    </div>

    <span v-if="slots['icon-end']" class="relative ml-2">
      <slot name="icon-end" />
    </span>
  </component>

  <UIButtonOld
    v-else
    :type="props.type"
    :theme="props.theme"
    :color="props.color"
    :is-loading="props.isLoading"
    :is-disabled="props.isDisabled"
    :is-icon="props.isIcon"
  >
    <template #icon-start>
      <slot name="icon-start" />
    </template>

    <slot />

    <template #icon-end>
      <slot name="icon-end" />
    </template>
  </UIButtonOld>
</template>
